import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";

import { useCallToActionQuery } from "api/callToActionApi.slice";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { getUserRole } from "domains/authentication/selectors";
import { CallToAction } from "domains/callsToAction/parts/CallToAction";
import { callsToActionSlice } from "domains/callsToAction/slice";
import type { CallToActionId, CallToActionStory } from "domains/callsToAction/types";
import { getUnusedCoachmarksInDOM } from "domains/coachmarks/selectors";
import { getActiveModals } from "domains/modal/selectors";
import { UserRole } from "entities/permissions/constants";

export const CallsToAction = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const dismissed = useSelector((state) => state.callsToAction.dismissed);
  const activeModals = useSelector(getActiveModals);
  const coachmarksInDom = useSelector(getUnusedCoachmarksInDOM);
  const userRole = useSelector(getUserRole);
  const [active, setActive] = useState<{ call: CallToActionStory; element: Element } | undefined>(undefined);

  const topic_id = params["topicId"];
  const callToActionQuery = useCallToActionQuery(topic_id === undefined ? skipToken : { topic_id });

  useEffect(() => {
    const calls = callToActionQuery.data?.manifest.stories.filter((call) => {
      const sub = call.dependencies.find((item) => item.type === "subscription");
      const userRoleMatches = sub === undefined ? userRole === UserRole.Public : userRole.toLowerCase() === sub.version;
      return !callIsDismissed(call.id, dismissed) && userRoleMatches;
    });

    if (calls === undefined || calls.length === 0) {
      setActive(undefined);
      return;
    }

    const callback = () => {
      for (const call of calls) {
        const element = document.querySelector(call.target_selector);
        if (element !== null && !callIsDismissed(call.id, dismissed)) {
          setActive({ call, element });
          return;
        }
      }
      setActive(undefined);
    };

    callback();
    const observer = new MutationObserver(callback);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [callToActionQuery.data?.manifest.stories, dismissed, userRole]);

  return active === undefined || coachmarksInDom.length > 0 || activeModals.length > 0 ? null : (
    <CallToAction
      callToAction={active.call}
      onClose={() => dispatch(callsToActionSlice.actions.dismiss(active.call.id))}
      target={active.element}
    />
  );
};

const callIsDismissed = (callId: CallToActionId, dismissed: [CallToActionId, number][]) => {
  const entry = dismissed.find(([id]) => id === callId);
  return entry !== undefined && new Date().getTime() - entry[1] < THIRTY_DAYS;
};

const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
